import React, { useState, useEffect } from 'react';
import '../Properties/property.scss';
import PropTypes from 'prop-types';
import PropertyLayout from './layout';
import PersonIcon from '@mui/icons-material/Person';
import useApiState from '../../Hooks/useApiState';
import LoaderOverlay from '../../Components/Loader/LoaderOverlay';

const initialState = {
  contactName: '',
  phoneNumber: '',
  email: '',
};

function ContactDetailsForm({ prevStep, onNext, onSave: _onSave, onBackToDashboard, initialValues = {}, step = 8, totalSteps = 10, systemId, isView = false }) {
  // onSave is part of the component API but not used in this implementation
  void _onSave; // Explicitly mark as intentionally unused
  const {
    isSubmitting,
    submitError,
    clearErrors,
    submitData,
  } = useApiState({
    showToastOnError: true,
    defaultErrorMessage: 'Failed to save contact details',
  });

  const [form, setForm] = useState({ ...initialState, ...initialValues });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialValues) {
      setForm((prev) => ({ ...prev, ...initialValues }));
    }
  }, [initialValues]);

  const validate = () => {
    const errors = {};
    if (!form.contactName) errors.contactName = 'Owner Name is required';
    if (!form.phoneNumber) errors.phoneNumber = 'Phone Number is required';
    if (form.phoneNumber && (!/^\d{10}$/.test(form.phoneNumber) || isNaN(form.phoneNumber))) {
      errors.phoneNumber = 'Phone Number must be a 10-digit number';
    }
    if (form.email && !/^([a-zA-Z0-9_\-.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,5})$/.test(form.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!form.caretakerIsOwner && form.caretakerPhoneNumber && (!/^\d{10}$/.test(form.caretakerPhoneNumber) || isNaN(form.caretakerPhoneNumber))) {
      errors.caretakerPhoneNumber = 'Phone Number must be a 10-digit number';
    }
    if (!form.caretakerIsOwner && form.caretakerEmail && !/^([a-zA-Z0-9_\-.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,5})$/.test(form.caretakerEmail)) {
      errors.caretakerEmail = 'Please enter a valid email address';
    }
    return errors;
  };

  const handleChange = (event) => {
    if (isView) return; // Prevent changes in view mode
    clearErrors();
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    clearErrors();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length === 0) {
      try {
        await submitData(async () => {
          return form;
        }, {
          successMessage: 'Contact details saved successfully',
        });

        onNext(form);
      } catch (error) {
        console.error('Failed to save contact details:', error);
      }
    }
  };

  const handlePrev = () => {
    clearErrors();
    prevStep();
  };

  return (
    <>
      <LoaderOverlay isVisible={isSubmitting} message="Saving contact details..." />

      <PropertyLayout step={step} totalSteps={totalSteps} systemId={systemId} onBackToDashboard={onBackToDashboard}>
        <div className="card">
          <h3 className="card-title">
            <PersonIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
            Contact Details
          </h3>

          {/* Display API errors */}
          {submitError && (
            <div className="error-message" style={{
              padding: '10px',
              marginBottom: '15px',
              backgroundColor: '#ffebee',
              color: '#c62828',
              borderRadius: '4px',
              border: '1px solid #ffcdd2',
            }}>
              {submitError}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <h4 className="header-title">
              Owner Details
            </h4>
            <div className="form-row-three">
              <div className="form-group">
                <label>Owner Name</label>
                <input
                  type="text"
                  name="contactName"
                  value={form.contactName}
                  onChange={handleChange}
                  placeholder="Enter contact name"
                  disabled={isSubmitting || isView}
                  readOnly={isView}
                />
                {errors.contactName && <div className="field-note-error">{errors.contactName}</div>}
              </div>
              <div className="form-group">
                <label>Owner Phone Number</label>
                <div className="phone-input-container">
                  <span className="phone-prefix">+91</span>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={form.phoneNumber}
                    onChange={handleChange}
                    placeholder="Enter phone number"
                    className="phone-input"
                    disabled={isSubmitting || isView}
                    readOnly={isView}
                  />
                </div>
                {errors.phoneNumber && <div className="field-note-error">{errors.phoneNumber}</div>}
              </div>
              <div className="form-group">
                <label>Owner Email</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  disabled={isSubmitting || isView}
                  readOnly={isView}
                />
                {errors.email && <div className="field-note-error">{errors.email}</div>}
              </div>
            </div>

            <h4 className="header-title" style={{ marginTop: '24px' }}>
              Caretaker Details
            </h4>
            <div className="form-row" style={{ margin: '16px 0 8px 0' }}>
              <label style={{ display: 'flex', alignItems: 'center', fontWeight: 500, fontSize: 15 }}>
                <input
                  type="checkbox"
                  name="caretakerIsOwner"
                  checked={form.caretakerIsOwner || false}
                  onChange={event => {
                    if (isView) return; // Prevent changes in view mode
                    const checked = event.target.checked;
                    setForm(prev => ({
                      ...prev,
                      caretakerIsOwner: checked,
                      caretakerName: checked ? prev.contactName : '',
                      caretakerPhoneNumber: checked ? prev.phoneNumber : '',
                      caretakerEmail: checked ? prev.email : '',
                    }));
                  }}
                  style={{ marginRight: 8 }}
                  disabled={isSubmitting || isView}
                />
                Caretaker is same as owner
              </label>
            </div>

            {!form.caretakerIsOwner && (
              <div className="form-row-three">
                <div className="form-group">
                  <label>Caretaker Name</label>
                  <input
                    type="text"
                    name="caretakerName"
                    value={form.caretakerName}
                    onChange={handleChange}
                    placeholder="Enter caretaker name"
                    disabled={isSubmitting || isView}
                    readOnly={isView}
                  />
                  {errors.caretakerName && <div className="field-note-error">{errors.caretakerName}</div>}
                </div>
                <div className="form-group">
                  <label>Caretaker Phone Number</label>
                  <div className="phone-input-container">
                    <span className="phone-prefix">+91</span>
                    <input
                      type="tel"
                      name="caretakerPhoneNumber"
                      value={form.caretakerPhoneNumber}
                      onChange={handleChange}
                      placeholder="Enter phone number"
                      className="phone-input"
                      disabled={isSubmitting || isView}
                      readOnly={isView}
                    />
                  </div>
                  {errors.caretakerPhoneNumber && <div className="field-note-error">{errors.caretakerPhoneNumber}</div>}
                </div>
                <div className="form-group">
                  <label>Caretaker Email</label>
                  <input
                    type="email"
                    name="caretakerEmail"
                    value={form.caretakerEmail}
                    onChange={handleChange}
                    placeholder="Enter email address"
                    disabled={isSubmitting || isView}
                    readOnly={isView}
                  />
                  {errors.caretakerEmail && <div className="field-note-error">{errors.caretakerEmail}</div>}
                </div>
              </div>
            )}

            <div className="form-actions">
              <button type="button" className="btn secondary" onClick={handlePrev} disabled={isSubmitting}>
                Previous
              </button>
              {isView ? (
                <button
                  type='button'
                  className='btn primary'
                  onClick={() => onNext(form)}
                >
                  Next
                </button>
              ) : (
                <button type="submit" className="btn primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Next'}
                </button>
              )}
            </div>
          </form>
        </div>
      </PropertyLayout>
    </>
  );
}

ContactDetailsForm.propTypes = {
  prevStep: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onBackToDashboard: PropTypes.func,
  initialValues: PropTypes.object,
  step: PropTypes.number,
  totalSteps: PropTypes.number,
  systemId: PropTypes.string,
  isView: PropTypes.bool,
};

export default ContactDetailsForm;
